import React, { useState } from 'react';
import Alert from '@cloudscape-design/components/alert';
import Badge from '@cloudscape-design/components/badge';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Container from '@cloudscape-design/components/container';
import ContentLayout from '@cloudscape-design/components/content-layout';
import FormField from '@cloudscape-design/components/form-field';
import Header from '@cloudscape-design/components/header';
import Input from '@cloudscape-design/components/input';
import SpaceBetween from '@cloudscape-design/components/space-between';
import { apiJson } from '../utils/api';

interface Registration {
  n_number: string;
  registration_country?: string;
  make?: string;
  model?: string;
  year_mfr?: number;
  registrant_city?: string;
  registrant_state?: string;
  engine_make?: string;
  engine_model?: string;
  status_code?: string;
}

const countryName = (c?: string): string =>
  c === 'CA' ? 'Canada' : c === 'US' ? 'United States' : (c ?? '—');

interface StoryData {
  story?: string;
  story_highlights?: string[];
  notable_missions?: string[];
  story_status?: string;
  nose_name?: string;
}

const Pair: React.FC<{ label: string; value?: string | number }> = ({ label, value }) => (
  <div>
    <Box variant="awsui-key-label">{label}</Box>
    <div>{value ?? '—'}</div>
  </div>
);

/** Renders one story the way the app shows it (lifts a leading "### Title"). */
const StoryBlock: React.FC<{ heading?: string; data: StoryData }> = ({ heading, data }) => {
  const text = data.story ?? '';
  const nl = text.indexOf('\n');
  const title = (nl === -1 ? text : text.slice(0, nl)).replace(/^#+\s*/, '').trim();
  const body = (nl === -1 ? '' : text.slice(nl + 1)).trim();
  const published = (data.story_status ?? 'pending') === 'approved';
  return (
    <SpaceBetween size="xs">
      {heading && (
        <Box variant="h4">
          {heading} <Badge color={published ? 'green' : 'grey'}>{data.story_status ?? 'pending'}</Badge>
        </Box>
      )}
      {title && <Box variant="h3">{title}</Box>}
      <Box variant="p">
        <span style={{ whiteSpace: 'pre-wrap' }}>{body || text}</span>
      </Box>
      {data.story_highlights && data.story_highlights.length > 0 && (
        <ul>
          {data.story_highlights.map((h, i) => (
            <li key={i}>{h}</li>
          ))}
        </ul>
      )}
      {data.notable_missions && data.notable_missions.length > 0 && (
        <>
          <Box variant="h4">Notable missions</Box>
          <ul>
            {data.notable_missions.map((m, i) => (
              <li key={i}>{m}</li>
            ))}
          </ul>
        </>
      )}
    </SpaceBetween>
  );
};

const RegistryPage: React.FC = () => {
  const [nNumber, setNNumber] = useState('');
  const [reg, setReg] = useState<Registration | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [looking, setLooking] = useState(false);

  const [typeStory, setTypeStory] = useState<StoryData | null>(null);
  const [airframeStory, setAirframeStory] = useState<StoryData | null>(null);
  const [noseInput, setNoseInput] = useState('');
  const [generating, setGenerating] = useState(false);
  const [genMsg, setGenMsg] = useState<string | null>(null);

  const [ingestMsg, setIngestMsg] = useState<string | null>(null);
  const [ingesting, setIngesting] = useState<'us' | 'ca' | null>(null);

  const loadStories = async (
    r: Registration,
  ): Promise<{ type: StoryData | null; airframe: StoryData | null }> => {
    const typeKey = `${(r.make ?? '').toLowerCase().trim()}|${(r.model ?? '').toLowerCase().trim()}`;
    let t: StoryData | null = null;
    let a: StoryData | null = null;
    try {
      t = await apiJson<StoryData>(`/admin/stories/${encodeURIComponent(typeKey)}`);
    } catch {
      // no type story yet
    }
    try {
      if (r.n_number) {
        a = await apiJson<StoryData>(`/admin/airframe-stories/${encodeURIComponent(r.n_number)}`);
      }
    } catch {
      // no airframe story yet
    }
    setTypeStory(t);
    setAirframeStory(a);
    return { type: t, airframe: a };
  };

  const lookup = async (): Promise<void> => {
    setError(null);
    setReg(null);
    setTypeStory(null);
    setAirframeStory(null);
    setGenMsg(null);
    setLooking(true);
    try {
      const data = await apiJson<{ registration: Registration }>(
        `/registrations/${encodeURIComponent(nNumber.trim())}`,
      );
      setReg(data.registration);
      void loadStories(data.registration);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLooking(false);
    }
  };

  const generate = async (): Promise<void> => {
    if (!reg) return;
    setGenerating(true);
    setGenMsg('Generating… this takes ~40–60s.');
    try {
      await apiJson('/admin/generate-story', {
        method: 'POST',
        body: JSON.stringify({
          make: reg.make,
          model: reg.model,
          n_number: reg.n_number,
          nose_name: noseInput.trim() || undefined,
        }),
      });
      for (let i = 0; i < 10; i++) {
        await new Promise((res) => setTimeout(res, 6000));
        const { type, airframe } = await loadStories(reg);
        if (type || airframe) {
          setGenMsg('Story ready (auto-review status shown below).');
          break;
        }
        if (i === 9) setGenMsg('Still generating — give it a moment and look up again.');
      }
    } catch (e) {
      setGenMsg(`Error: ${(e as Error).message}`);
    } finally {
      setGenerating(false);
    }
  };

  const triggerIngest = async (country: 'us' | 'ca'): Promise<void> => {
    setIngestMsg(null);
    setIngesting(country);
    try {
      const data = await apiJson<{ message: string }>('/admin/ingest/registry', {
        method: 'POST',
        body: JSON.stringify({ country }),
      });
      setIngestMsg(data.message);
    } catch (e) {
      setIngestMsg(`Error: ${(e as Error).message}`);
    } finally {
      setIngesting(null);
    }
  };

  return (
    <ContentLayout header={<Header variant="h1">Aircraft Registry</Header>}>
      <SpaceBetween size="l">
        <Container header={<Header variant="h2">Tail-number lookup</Header>}>
          <SpaceBetween size="l">
            <FormField label="Registration mark (US or Canada)">
              <SpaceBetween size="xs" direction="horizontal">
                <Input
                  value={nNumber}
                  onChange={(e) => setNNumber(e.detail.value)}
                  placeholder="N172SP or C-GABC"
                  onKeyDown={(e) => {
                    if (e.detail.key === 'Enter' && nNumber.trim()) void lookup();
                  }}
                />
                <Button onClick={() => void lookup()} loading={looking} disabled={!nNumber.trim()}>
                  Look up
                </Button>
              </SpaceBetween>
            </FormField>

            {error && <Alert type="error">{error}</Alert>}

            {reg && (
              <ColumnLayout columns={3} variant="text-grid">
                <Pair label="Mark" value={reg.n_number} />
                <Pair label="Country" value={countryName(reg.registration_country)} />
                <Pair label="Make" value={reg.make} />
                <Pair label="Model" value={reg.model} />
                <Pair label="Year" value={reg.year_mfr} />
                <Pair
                  label="Engine"
                  value={[reg.engine_make, reg.engine_model].filter(Boolean).join(' ')}
                />
                <Pair label="Status" value={reg.status_code} />
              </ColumnLayout>
            )}
          </SpaceBetween>
        </Container>

        {reg && (
          <Container
            header={
              <Header
                variant="h2"
                description="The story as the app shows it (status badge shows publish state). Generate it here to test without the phone."
              >
                Story preview
              </Header>
            }
          >
            <SpaceBetween size="l">
              {!typeStory && !airframeStory && (
                <Box color="text-body-secondary">
                  No story generated yet for this aircraft. Optionally add the painted nose name,
                  then Generate.
                </Box>
              )}
              {airframeStory?.story && <StoryBlock heading="✈ This aircraft" data={airframeStory} />}
              {typeStory?.story && <StoryBlock heading="About the type" data={typeStory} />}

              <FormField
                label="Generate / regenerate"
                description="Nose name (optional) seeds the airframe-specific story — e.g. “Aluminum Overcast”."
              >
                <SpaceBetween size="xs" direction="horizontal">
                  <Input
                    value={noseInput}
                    onChange={(e) => setNoseInput(e.detail.value)}
                    placeholder="Nose name (optional)"
                  />
                  <Button onClick={() => void generate()} loading={generating} iconName="gen-ai">
                    Generate
                  </Button>
                </SpaceBetween>
              </FormField>
              {genMsg && (
                <Alert type={genMsg.startsWith('Error') ? 'error' : 'info'}>{genMsg}</Alert>
              )}
            </SpaceBetween>
          </Container>
        )}

        <Container
          header={
            <Header
              variant="h2"
              description="Re-run a government registry ingestion now (both also run on a schedule): the US FAA Releasable Aircraft Database, or the Transport Canada CCARCS."
            >
              Ingestion
            </Header>
          }
        >
          <SpaceBetween size="s">
            <SpaceBetween size="xs" direction="horizontal">
              <Button
                onClick={() => void triggerIngest('us')}
                loading={ingesting === 'us'}
                disabled={ingesting !== null}
                iconName="refresh"
              >
                Refresh US (FAA) registry
              </Button>
              <Button
                onClick={() => void triggerIngest('ca')}
                loading={ingesting === 'ca'}
                disabled={ingesting !== null}
                iconName="refresh"
              >
                Refresh Canadian (CCARCS) registry
              </Button>
            </SpaceBetween>
            {ingestMsg && (
              <Alert type={ingestMsg.startsWith('Error') ? 'error' : 'success'}>{ingestMsg}</Alert>
            )}
          </SpaceBetween>
        </Container>
      </SpaceBetween>
    </ContentLayout>
  );
};

export default RegistryPage;
